import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import PlayerAvatar from "../components/PlayerAvatar";
import Stars from "../components/Stars";
import { ArrowLeft, Plus, Minus, FloppyDisk } from "@phosphor-icons/react";

const COUNTER_STATS = [
  { key: "goals", label: "Goals", color: "#D90429" },
  { key: "assists", label: "Assists", color: "#FFD166" },
  { key: "shots", label: "Shots", color: "#F5F5F5" },
  { key: "shots_on_target", label: "On Target", color: "#06D6A0" },
  { key: "tackles", label: "Tackles", color: "#F5F5F5" },
  { key: "interceptions", label: "Intercepts", color: "#F5F5F5" },
  { key: "yellow_cards", label: "Yellow", color: "#FFD166" },
  { key: "red_cards", label: "Red", color: "#D90429" },
];

const PRESETS = [30, 45, 60, 90];

const emptyStats = () => ({
  goals: 0, assists: 0, shots: 0, shots_on_target: 0, tackles: 0,
  interceptions: 0, yellow_cards: 0, red_cards: 0, minutes: 0, coach_rating: 0,
});

export default function MatchStats() {
  const { id } = useParams();
  const [match, setMatch] = useState(null);
  const [players, setPlayers] = useState([]);
  const [statsByPlayer, setStatsByPlayer] = useState({});
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    (async () => {
      const [ms, ps, ss] = await Promise.all([
        api.get("/matches"),
        api.get("/players"),
        api.get(`/match-stats/${id}`),
      ]);
      setMatch(ms.data.find((m) => m.id === id));
      setPlayers(ps.data);
      const map = {};
      ss.data.forEach((s) => { map[s.player_id] = s; });
      setStatsByPlayer(map);
    })();
  }, [id]);

  const activeStats = selected ? (statsByPlayer[selected] || { ...emptyStats(), player_id: selected, match_id: id }) : null;

  function updateStat(k, v) {
    setStatsByPlayer((prev) => ({
      ...prev,
      [selected]: { ...(prev[selected] || { ...emptyStats(), player_id: selected, match_id: id }), [k]: v },
    }));
  }
  function inc(k) { updateStat(k, Math.max(0, (activeStats?.[k] || 0) + 1)); }
  function dec(k) { updateStat(k, Math.max(0, (activeStats?.[k] || 0) - 1)); }

  async function save() {
    if (!selected) return;
    try {
      await api.post("/match-stats", { ...activeStats, match_id: id, player_id: selected });
      toast.success("Stats saved");
    } catch (e) { toast.error(formatApiError(e)); }
  }

  if (!match) return <div className="overline">Loading…</div>;

  return (
    <div className="space-y-6">
      <Link to="/matches" className="flex items-center gap-2 text-neutral-400 hover:text-white text-sm">
        <ArrowLeft size={16} weight="bold" /> Matches
      </Link>

      <div>
        <div className="overline">Match Stats</div>
        <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight mt-1">vs {match.opponent}</h1>
        <div className="overline mt-1">{match.match_date} {match.match_time && `• ${match.match_time}`} {match.location && `• ${match.location}`}</div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Player list */}
        <div className="bg-[#121212] border border-neutral-800 rounded-lg p-3 max-h-[70vh] overflow-y-auto">
          <div className="overline p-2">Squad</div>
          {players.map((p) => {
            const has = statsByPlayer[p.id];
            const isActive = selected === p.id;
            return (
              <button
                key={p.id}
                data-testid={`ms-select-${p.id}`}
                onClick={() => setSelected(p.id)}
                className={`w-full flex items-center gap-3 p-2 rounded-md text-left ${isActive ? "bg-[#D90429] text-white" : "hover:bg-neutral-900 text-neutral-200"}`}
              >
                <PlayerAvatar player={p} size={40} />
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold uppercase tracking-tight text-sm leading-tight truncate">
                    {p.first_name} {p.last_name}
                  </div>
                  <div className={`text-[10px] uppercase tracking-widest font-bold ${isActive ? "text-white/80" : "text-neutral-500"}`}>
                    #{p.jersey_number || "—"} {has && "• saved"}
                  </div>
                </div>
                {has && <span className="w-2 h-2 rounded-full bg-[#06D6A0]" />}
              </button>
            );
          })}
        </div>

        {/* Stats panel */}
        <div className="lg:col-span-2 space-y-4">
          {!selected ? (
            <div className="bg-[#121212] border border-neutral-800 rounded-lg p-10 text-center overline">
              Select a player to record stats
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {COUNTER_STATS.map((s) => (
                  <div key={s.key} className="bg-[#121212] border border-neutral-800 rounded-lg p-3">
                    <div className="overline">{s.label}</div>
                    <div className="flex items-center justify-between mt-2">
                      <button
                        onClick={() => dec(s.key)}
                        data-testid={`ms-${s.key}-minus`}
                        className="w-11 h-11 rounded-md bg-[#0a0a0a] border border-neutral-800 hover:border-[#D90429] hover:text-[#D90429] flex items-center justify-center"
                      >
                        <Minus size={16} weight="bold" />
                      </button>
                      <div className="font-display font-black text-3xl" style={{ color: s.color }}>
                        {activeStats?.[s.key] || 0}
                      </div>
                      <button
                        onClick={() => inc(s.key)}
                        data-testid={`ms-${s.key}-plus`}
                        className="w-11 h-11 rounded-md bg-[#D90429] hover:bg-[#b8031f] text-white flex items-center justify-center"
                      >
                        <Plus size={16} weight="bold" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-[#121212] border border-neutral-800 rounded-lg p-4">
                <div className="overline mb-2">Minutes Played</div>
                <div className="flex items-center gap-3 flex-wrap">
                  <input
                    type="number"
                    className="w-24 h-12 px-3 bg-[#0a0a0a] border border-neutral-800 rounded-md focus:outline-none focus:border-[#D90429]"
                    value={activeStats?.minutes || 0}
                    onChange={(e) => updateStat("minutes", +e.target.value || 0)}
                    data-testid="ms-minutes-input"
                  />
                  {PRESETS.map((m) => (
                    <button
                      key={m}
                      onClick={() => updateStat("minutes", m)}
                      data-testid={`ms-preset-${m}`}
                      className="h-12 px-4 rounded-md border border-neutral-800 hover:border-[#06D6A0] hover:text-[#06D6A0] font-display font-bold"
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-[#121212] border border-neutral-800 rounded-lg p-4">
                <div className="overline mb-2">Coach Match Rating</div>
                <Stars value={activeStats?.coach_rating || 0} onChange={(v) => updateStat("coach_rating", v)} size={36} testid="ms-coach-rating" />
              </div>

              <button
                onClick={save}
                data-testid="ms-save-btn"
                className="w-full h-14 rounded-md bg-[#D90429] hover:bg-[#b8031f] text-white font-display font-bold uppercase tracking-wider flex items-center justify-center gap-2"
              >
                <FloppyDisk size={20} weight="bold" /> Save Stats
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
